import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { paymentOperationsRepository } from "../repositories";
const emptyList = () => ({
    items: [],
    pagination: { limit: 25, offset: 0, total: 0 },
});
export const usePaymentOperationsStore = defineStore("payment-operations", () => {
    const dashboard = ref(null);
    const requests = ref(emptyList());
    const reconciliation = ref(emptyList());
    const refunds = ref(emptyList());
    const payables = ref(emptyList());
    const payoutBatches = ref(emptyList());
    const onvoEvents = ref(emptyList());
    const tenantCollections = ref(emptyList());
    const tenantProviderPayables = ref(emptyList());
    const isLoading = ref(false);
    const isSaving = ref(false);
    const error = ref(null);
    const openRequestCount = computed(() => dashboard.value?.summary.open_requests ?? 0);
    const openReconciliationCount = computed(() => dashboard.value?.summary.open_reconciliation ?? 0);
    const openPayoutCount = computed(() => dashboard.value?.summary.open_payout_batches ?? 0);
    const openRefundCount = computed(() => dashboard.value?.summary.open_refunds ?? 0);
    async function fetchPlatformDashboard() {
        return withLoading(async () => {
            dashboard.value =
                await paymentOperationsRepository.getPlatformDashboard();
            return dashboard.value;
        });
    }
    async function fetchPlatformRequests(query = {}) {
        return withLoading(async () => {
            requests.value =
                await paymentOperationsRepository.listPlatformRequests(query);
            return requests.value;
        });
    }
    async function fetchPlatformReconciliation(query = {}) {
        return withLoading(async () => {
            reconciliation.value =
                await paymentOperationsRepository.listPlatformReconciliation(query);
            return reconciliation.value;
        });
    }
    async function fetchPlatformRefunds(query = {}) {
        return withLoading(async () => {
            refunds.value =
                await paymentOperationsRepository.listPlatformRefunds(query);
            return refunds.value;
        });
    }
    async function fetchPlatformPayables(query = {}) {
        return withLoading(async () => {
            payables.value =
                await paymentOperationsRepository.listPlatformPayables(query);
            return payables.value;
        });
    }
    async function fetchPlatformPayoutBatches(query = {}) {
        return withLoading(async () => {
            payoutBatches.value =
                await paymentOperationsRepository.listPlatformPayoutBatches(query);
            return payoutBatches.value;
        });
    }
    async function fetchPlatformOnvoEvents(query = {}) {
        return withLoading(async () => {
            onvoEvents.value =
                await paymentOperationsRepository.listPlatformOnvoEvents(query);
            return onvoEvents.value;
        });
    }
    async function fetchTenantCollections(query = {}) {
        return withLoading(async () => {
            tenantCollections.value =
                await paymentOperationsRepository.listTenantCollections(query);
            return tenantCollections.value;
        });
    }
    async function fetchTenantProviderPayables(query = {}) {
        return withLoading(async () => {
            tenantProviderPayables.value =
                await paymentOperationsRepository.listTenantProviderPayables(query);
            return tenantProviderPayables.value;
        });
    }
    async function requestRefund(paymentRequestId, payload, idempotencyKey) {
        return withSaving(async () => {
            const result = await paymentOperationsRepository.requestRefund(paymentRequestId, payload, idempotencyKey);
            await Promise.all([
                fetchPlatformDashboard(),
                fetchPlatformRefunds(),
                fetchPlatformRequests(),
            ]);
            return result;
        });
    }
    async function createPayoutBatch(payload, idempotencyKey) {
        return withSaving(async () => {
            const result = await paymentOperationsRepository.createPayoutBatch(payload, idempotencyKey);
            await Promise.all([
                fetchPlatformDashboard(),
                fetchPlatformPayables(),
                fetchPlatformPayoutBatches(),
            ]);
            return result;
        });
    }
    async function updatePayoutBatch(payoutBatchId, payload) {
        return withSaving(async () => {
            const result = await paymentOperationsRepository.updatePayoutBatch(payoutBatchId, payload);
            await Promise.all([
                fetchPlatformDashboard(),
                fetchPlatformPayables(),
                fetchPlatformPayoutBatches(),
            ]);
            return result;
        });
    }
    async function resolveReconciliation(reconciliationId, payload) {
        return withSaving(async () => {
            const result = await paymentOperationsRepository.resolveReconciliation(reconciliationId, payload);
            await Promise.all([
                fetchPlatformDashboard(),
                fetchPlatformReconciliation(),
            ]);
            return result;
        });
    }
    async function withLoading(callback) {
        isLoading.value = true;
        error.value = null;
        try {
            return await callback();
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not load payments";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function withSaving(callback) {
        isSaving.value = true;
        error.value = null;
        try {
            return await callback();
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "Could not save payment action";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    function $reset() {
        dashboard.value = null;
        requests.value = emptyList();
        reconciliation.value = emptyList();
        refunds.value = emptyList();
        payables.value = emptyList();
        payoutBatches.value = emptyList();
        onvoEvents.value = emptyList();
        tenantCollections.value = emptyList();
        tenantProviderPayables.value = emptyList();
        isLoading.value = false;
        isSaving.value = false;
        error.value = null;
    }
    return {
        dashboard,
        requests,
        reconciliation,
        refunds,
        payables,
        payoutBatches,
        onvoEvents,
        tenantCollections,
        tenantProviderPayables,
        isLoading,
        isSaving,
        error,
        openRequestCount,
        openReconciliationCount,
        openPayoutCount,
        openRefundCount,
        fetchPlatformDashboard,
        fetchPlatformRequests,
        fetchPlatformReconciliation,
        fetchPlatformRefunds,
        fetchPlatformPayables,
        fetchPlatformPayoutBatches,
        fetchPlatformOnvoEvents,
        fetchTenantCollections,
        fetchTenantProviderPayables,
        requestRefund,
        createPayoutBatch,
        updatePayoutBatch,
        resolveReconciliation,
        $reset,
    };
});
